import * as Sentry from '@sentry/react';
import { encodeEnvelope, parseEnvelope, encodeJson, type Envelope, EnvelopeError } from './envelope';
import { getClientId, getClientGroupId, CLIENT_PLATFORM } from './client-identity';
import { KIND_AUTH } from './envelope-kinds';
import { useWorkspaceStore } from '@/stores/use-workspace-store';

export type TransportStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'auth-error'
  | 'client-outdated';

/** WebSocket close code the server sends when the client speaks an older
 *  protocol version. Mirrors CLOSE_CODE_CLIENT_OUTDATED in the nexus handler. */
const CLOSE_CODE_CLIENT_OUTDATED = 4001;

type FrameHandler = (envelope: Envelope) => void;
type StatusHandler = (status: TransportStatus) => void;

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const DEFAULT_DRAIN_TIMEOUT_MS = 2000;
const DRAIN_POLL_MS = 50;

export class SyncTransport {
  private ws: WebSocket | null = null;
  private url: string | null = null;
  private token: string | null = null;
  private shareToken: string | null = null;
  private shareMode = false;
  private status: TransportStatus = 'disconnected';
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private frameHandlers = new Set<FrameHandler>();
  private statusHandlers = new Set<StatusHandler>();

  connect(url: string, token: string): void {
    this.url = url;
    this.token = token;
    this.shareMode = false;
    this.shareToken = null;
    this.openSocket();
  }

  connectWithShareToken(url: string, shareToken: string): void {
    this.url = url;
    this.shareToken = shareToken;
    this.shareMode = true;
    this.token = null;
    this.openSocket();
  }

  refreshAuth(newToken: string): void {
    this.token = newToken;
    if (this.ws && this.ws.readyState === 1) {
      this.sendAuthFrame();
    }
  }

  /**
   * Force a clean reconnect. Closes the current socket and reopens it using
   * the stored URL and token. The new AUTH frame will pick up the updated
   * activeWorkspaceId from useWorkspaceStore automatically.
   *
   * Called by the workspace-switch orchestrator (M10b) after
   * setActiveEntityStore() has swapped the entity store and
   * useWorkspaceStore has committed the new activeWorkspaceId.
   * No-op if the transport has never been connected (url is null).
   */
  reconnect(): void {
    if (!this.url) return;
    this.closeSocket();
    this.openSocket();
  }

  async disconnect(opts?: { drainTimeout?: number }): Promise<void> {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempt = 0;

    if (this.ws && this.ws.readyState === 1) {
      const deadline = Date.now() + (opts?.drainTimeout ?? DEFAULT_DRAIN_TIMEOUT_MS);
      while (this.ws.bufferedAmount > 0 && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, DRAIN_POLL_MS));
      }
      if (this.ws.bufferedAmount > 0) {
        console.warn(`[SyncTransport] drain timeout; ${this.ws.bufferedAmount} bytes still buffered`);
      }
    }
    this.closeSocket();
    this.setStatus('disconnected');
  }

  /** Send a raw envelope frame. Returns false if the socket is not open. */
  send(bytes: Uint8Array): boolean {
    if (!this.ws || this.ws.readyState !== 1) return false;
    try {
      this.ws.send(bytes);
    } catch {
      return false;
    }
    return true;
  }

  /**
   * Send a tiny data-frame heartbeat to keep Cloudflare's WebSocket proxy
   * from idling the connection out at ~100s. Control-frame Pings do not
   * reset the proxy's idle timer; a data frame does. Returns false if the
   * socket is not open.
   *
   * The server recognises this text frame shape (`{"type":"ping"}`) and
   * silently drops it — unlike arbitrary text frames, which close the
   * connection as a protocol violation.
   */
  sendHeartbeat(): boolean {
    if (!this.ws || this.ws.readyState !== 1) return false;
    try {
      this.ws.send('{"type":"ping"}');
    } catch {
      return false;
    }
    return true;
  }

  onFrame(handler: FrameHandler): () => void {
    this.frameHandlers.add(handler);
    return () => this.frameHandlers.delete(handler);
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  getStatus(): TransportStatus {
    return this.status;
  }

  isShareMode(): boolean {
    return this.shareMode;
  }

  /** Called by SyncSystemContext when AuthOk frame is received. */
  setAuthenticated(): void {
    this.setStatus('connected');
  }

  setAuthError(): void {
    this.setStatus('auth-error');
  }

  private sendAuthFrame(): void {
    if (!this.ws || this.ws.readyState !== 1) return;

    // M10b: workspaceId is required in every AUTH frame (M10a server enforcement).
    // Share-mode uses the share-token rail and doesn't need a workspaceId —
    // the server resolves workspace context from the share token itself.
    if (!this.shareMode) {
      const workspaceId = useWorkspaceStore.getState().activeWorkspaceId;
      if (workspaceId === null) {
        // Programmer error: the orchestrator must ensure activeWorkspaceId is set
        // before the WS connects. Log + skip the frame — the engine's reconnect
        // logic will retry once the orchestrator sets the workspace.
        const msg = '[SyncTransport] sendAuthFrame: activeWorkspaceId is null — AUTH frame not sent (programmer error: orchestrator must set workspace before WS connects)';
        console.error(msg);
        Sentry.captureMessage(msg, 'error');
        return;
      }
    }

    const corrId = crypto.randomUUID();
    const workspaceId = !this.shareMode
      ? useWorkspaceStore.getState().activeWorkspaceId!
      : undefined;

    const payload = this.shareMode && this.shareToken
      ? {
          token: '',
          shareToken: this.shareToken,
          clientId: getClientId(),
          clientGroupId: getClientGroupId(),
          clientPlatform: CLIENT_PLATFORM,
        }
      : {
          token: this.token ?? '',
          clientId: getClientId(),
          clientGroupId: getClientGroupId(),
          clientPlatform: CLIENT_PLATFORM,
          workspaceId,
        };
    const bytes = encodeEnvelope({
      kind: KIND_AUTH,
      corrId,
      json: encodeJson(payload),
    });
    this.ws.send(bytes);
  }

  private openSocket(): void {
    if (!this.url) return;
    // Close any existing socket before opening a new one. Without this,
    // repeated connect() calls (e.g. when auth token updates) leak sockets
    // that stay open in the background, each receiving updates independently
    // and racing to send mutations. The orphaned socket's onclose handler
    // also schedules a reconnect, fighting this fresh connection.
    this.closeSocket();
    this.setStatus('connecting');
    const ws = new WebSocket(this.url);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    ws.onopen = () => {
      // Ignore events from a socket that has been superseded (closed by a
      // subsequent openSocket). Without this guard, an orphan's onopen can
      // fire after the new socket is already connecting.
      if (this.ws !== ws) return;
      this.reconnectAttempt = 0;
      // Do NOT set status to 'connected' yet — wait for AuthOk envelope.
      // SyncSystemContext calls setAuthenticated() on AuthOk.
      this.sendAuthFrame();
    };
    ws.onclose = (ev: CloseEvent) => {
      if (this.ws !== ws) return; // orphan — ignore
      this.ws = null;

      if (ev.code === CLOSE_CODE_CLIENT_OUTDATED) {
        // Terminal: this client speaks an older protocol than the server.
        // Reconnecting would just loop against the same rejection — surface an
        // "update required" state instead and stop.
        this.setStatus('client-outdated');
        return;
      }

      this.setStatus('disconnected');
      if (this.url) {
        if (ev.code === 1001) {
          // Server restarting — reconnect immediately, no backoff
          this.reconnectAttempt = 0;
          this.scheduleReconnect();
        } else {
          this.scheduleReconnect();
        }
      }
    };
    ws.onerror = () => {
      // onclose fires after onerror; reconnect is handled there
    };
    ws.onmessage = (ev: MessageEvent) => {
      if (this.ws !== ws) return; // orphan — drop messages

      // Reject text frames — envelope protocol is binary-only.
      if (typeof ev.data === 'string') {
        console.warn('[SyncTransport] dropped unexpected text frame');
        return;
      }

      const bytes = new Uint8Array(ev.data as ArrayBuffer);
      try {
        const envelope = parseEnvelope(bytes);
        this.frameHandlers.forEach((fn) => fn(envelope));
      } catch (err) {
        if (err instanceof EnvelopeError) {
          console.warn(
            `[SyncTransport] envelope parse failed: ${err.code} — ${err.message}`,
          );
        } else {
          Sentry.captureException(err);
          console.error('[SyncTransport] unexpected parse error', err);
        }
      }
    };
  }

  private closeSocket(): void {
    if (this.ws) {
      try {
        this.ws.close(1000, 'client-disconnect');
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    const base = Math.min(RECONNECT_BASE_MS * 2 ** this.reconnectAttempt, RECONNECT_MAX_MS);
    const jitter = Math.random() * 1000;
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, base + jitter);
  }

  private setStatus(next: TransportStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.statusHandlers.forEach((fn) => fn(next));
  }
}
